import {OperationTypes} from '@jimmyn/apollo-client';
import {Card, Col, Row} from 'antd';
import {
  Interval,
  IntervalInput,
  useCreateSubscriptionMutation,
  useSubscriptionQuery,
  useUpdateSubscriptionMutation
} from 'api/generated';
import {subscriptionsQuery} from 'api/queries';
import {PageHeader} from 'components';
import {Formik} from 'formik';
import {useMediaQuery} from 'hooks/useMediaQuery';
import {omitEmpty} from 'lib/omitEmpty';
import {amountFromInt, amountToInt} from 'lib/utils';
import React from 'react';
import {RouteComponentProps, useHistory} from 'react-router-dom';
import {EditSubscriptionForm, FormValues} from './EditSubscriptionForm';

type Props = {
  accountId: string;
  subscriptionId: string;
};

export const EditSubscription: React.FC<RouteComponentProps<Props>> = ({match}) => {
  const {subscriptionId} = match.params;
  const history = useHistory();

  const {isTablet} = useMediaQuery();

  const {data, loading} = useSubscriptionQuery({
    skip: !subscriptionId,
    variables: {id: subscriptionId}
  });
  const [updateSubscription] = useUpdateSubscriptionMutation();
  const [createSubscription] = useCreateSubscriptionMutation({
    updateQuery: {query: subscriptionsQuery},
    operationType: OperationTypes.ADD
  });

  const subscription = data?.subscription;

  const initialValues: FormValues = {
    accountId: match.params.accountId,
    amount: amountFromInt(subscription?.amount || 0),
    customer: subscription?.customer || {},
    description: subscription?.description,
    interval: (subscription?.interval as unknown as IntervalInput) || Interval.MONTH,
    intervalCount: subscription?.intervalCount || 1,
    billingDetails: subscription?.billingDetails || {},
    shippingDetails: subscription?.shippingDetails || {},
    trialPeriodDays: subscription?.trialPeriodDays
  };

  const handleSubmit = async ({amount, storeId, trialPeriodDays, ...input}: FormValues) => {
    if (!subscriptionId) {
      const {data} = await createSubscription({
        variables: {
          input: omitEmpty({
            ...input,
            amount: amountToInt(amount),
            trialPeriodDays,
            storeId
          })
        }
      });
      const id = data?.createSubscription?.id;
      if (!id) return;
      history.push(`/accounts/${match.params.accountId}/subscriptions/${id}`, {
        action: 'sendSubscriptionStatus'
      });
    } else {
      await updateSubscription({
        variables: {
          input: omitEmpty({
            ...input,
            amount: amountToInt(amount),
            id: subscriptionId
          })
        }
      });
      history.push(`/accounts/${match.params.accountId}/subscriptions/${subscriptionId}`);
    }
  };

  return (
    <>
      <Row>
        <Col span={isTablet ? 24 : 12} offset={isTablet ? 0 : 6}>
          <PageHeader
            title={subscriptionId ? 'Update subscription' : 'Create subscription'}
            loading={loading}
          />
          {loading ? (
            <Card loading />
          ) : (
            <Formik initialValues={initialValues} onSubmit={handleSubmit}>
              <EditSubscriptionForm isNew={!subscriptionId} accountId={match.params.accountId} />
            </Formik>
          )}
        </Col>
      </Row>
    </>
  );
};
